/**
 * The run summary (TRD-09 §11) — shown the moment a run ends.
 *
 * Everything here was captured from the world at the tick it ended: the score breakdown
 * uses the same formula the sim scores with (R-06-23), damage-by-tower reads the
 * `damageDealt` the pipeline attributed hit by hit (R-09-26), and the Shard lines are the
 * award the meta system just banked. No number on this screen is recomputed by the UI.
 *
 * The bar chart is a ranked horizontal bar list: identity carried by each tower's class
 * colour (the game's validated categorical palette), values and names in ink — text
 * never wears the series colour — thin bars with rounded data-ends on a shared zero
 * baseline, 2px gaps.
 */
import { CLASS_COLORS, getClass, getTower } from '@prism/content';
import type { ClassId, UiSnapshot } from '@prism/sim';
import type { ShardAward } from '../meta/meta.js';
import { submitMessage, type SubmitOutcome } from '../persistence/submission.js';

export interface RunSummaryData {
  victory: boolean;
  wavesCleared: number;
  totalWaves: number;
  score: number;
  goldEarned: number;
  coreHp: number;
  coreMaxHp: number;
  towers: UiSnapshot['placedTowers'];
  synergies: UiSnapshot['synergies'];
  mode: 'daily' | 'free';
  /** The daily's mutator name, so the summary explains the day's twist (B-1). */
  mutator: string | null;
}

interface RunSummaryProps {
  data: RunSummaryData;
  award: ShardAward | null;
  /**
   * What happened to this run's leaderboard submission. `null` when the run was not a
   * daily, or was not worth sending because it did not beat one already sent today.
   *
   * Shown because it was invisible: a rejected submission reduced to a boolean and
   * dropped, which is how "my runs never save" stayed unexplained for three rounds of
   * reports. If it did not count, the summary now says so and says why.
   */
  submission: SubmitOutcome | 'sending' | null;
  /**
   * The feedback form, when it is due. A slot rather than props so this component stays
   * a readout of the run — whether feedback is owed today is App's question, not its.
   */
  feedback?: React.ReactNode;
  onPlayAgain: () => void;
  onMenu: () => void;
  onClose: () => void;
}

/** The sim's scoring formula (R-06-23), broken into the lines a player can act on. */
export function scoreLines(data: RunSummaryData): { label: string; value: number }[] {
  return [
    { label: `${data.wavesCleared} waves × 1000`, value: data.wavesCleared * 1000 },
    { label: 'Gold earned over the run', value: data.goldEarned },
    { label: `${data.coreHp} Core HP × 100`, value: data.coreHp * 100 },
  ];
}

export function RunSummary({
  data,
  award,
  submission,
  feedback,
  onPlayAgain,
  onMenu,
  onClose,
}: RunSummaryProps) {
  // Assist-only towers (supports) rank by what they enabled, not by their zero — a
  // Signal Lens showing "0" next to the towers it buffed all game reads as broken.
  const towers = [...data.towers].sort(
    (a, b) => b.damageDealt - a.damageDealt || b.assistDealt - a.assistDealt,
  );
  const maxDamage = Math.max(1, ...towers.map((t) => Math.max(t.damageDealt, t.assistDealt)));
  const lines = scoreLines(data);
  const active = data.synergies.filter((s) => s.count > 0);

  return (
    <div className="summary-overlay">
      <div className="summary">
        <header className="summary__head">
          <b className={data.victory ? 'summary__verdict--win' : 'summary__verdict--loss'}>
            {data.victory ? 'VICTORY' : 'RUN OVER'}
          </b>
          <span className="muted">
            wave {data.wavesCleared} / {data.totalWaves}
            {data.mutator && ` · ☄ ${data.mutator}`}
          </span>
          <span className="spacer" />
          <span className="summary__score">{data.score.toLocaleString()} pts</span>
        </header>

        <div className="summary__columns">
          <section>
            <span className="label">Score</span>
            {lines.map((line) => (
              <div key={line.label} className="summary__row">
                <span className="muted">{line.label}</span>
                <span>{line.value.toLocaleString()}</span>
              </div>
            ))}
            {award && (
              <>
                <span className="label">Shards earned</span>
                {award.breakdown.map((b) => (
                  <div key={b.reason} className="summary__row">
                    <span className="muted">{b.reason}</span>
                    <span className="meta-shards">+{b.shards}</span>
                  </div>
                ))}
              </>
            )}
            {active.length > 0 && (
              <>
                <span className="label">Final board</span>
                <div className="summary__synergies">
                  {active.map((sy) => {
                    const cls = getClass(sy.classId);
                    return (
                      <span
                        key={sy.classId}
                        className="class-chip"
                        style={{
                          color: CLASS_COLORS[sy.classId].color,
                          borderColor: CLASS_COLORS[sy.classId].color,
                        }}
                      >
                        <b>{cls.glyph}</b> {cls.name} {sy.count}
                        {sy.activeTier > 0 ? ` ✦${sy.activeTier}` : ''}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          <section>
            <span className="label">Damage by tower</span>
            {towers.length === 0 && <span className="muted">Nothing was placed.</span>}
            {towers.map((t) => {
              const def = getTower(t.defId);
              const primary = def.classes[0] as ClassId;
              const assistOnly = t.damageDealt === 0 && t.assistDealt > 0;
              const value = assistOnly ? t.assistDealt : t.damageDealt;
              return (
                <div
                  key={t.id}
                  className="summary__bar-row"
                  title={
                    assistOnly
                      ? `${def.name}${t.star > 1 ? ` ★${t.star}` : ''} — assisted ${t.assistDealt.toLocaleString()} damage through its aura`
                      : `${def.name}${t.star > 1 ? ` ★${t.star}` : ''} — ${t.damageDealt.toLocaleString()} damage`
                  }
                >
                  <span className="summary__bar-name">
                    <b style={{ color: CLASS_COLORS[primary].color }}>{def.glyph}</b> {def.name}
                    {t.star > 1 ? ` ★${t.star}` : ''}
                  </span>
                  <span className="summary__bar-track">
                    <span
                      className="summary__bar-fill"
                      style={{
                        width: `${Math.max(1, (value / maxDamage) * 100)}%`,
                        background: CLASS_COLORS[primary].color,
                        opacity: assistOnly ? 0.45 : 1,
                      }}
                    />
                  </span>
                  <span className={`summary__bar-value${assistOnly ? ' muted' : ''}`}>
                    {value.toLocaleString()}
                    {assistOnly ? ' assisted' : ''}
                  </span>
                </div>
              );
            })}
          </section>
        </div>

        {submission !== null && (
          <p
            className={`summary__submit${
              submission !== 'sending' && submitMessage(submission).tone === 'warn'
                ? ' summary__submit--warn'
                : ''
            }`}
          >
            {submission === 'sending' ? 'Submitting…' : submitMessage(submission).text}
          </p>
        )}

        {feedback}

        <footer className="summary__actions">
          <button type="button" className="primary" onClick={onPlayAgain}>
            {data.mode === 'daily' ? '↻ Replay this daily' : '↻ New run'}
          </button>
          <button type="button" onClick={onMenu}>
            Main menu
          </button>
          <button type="button" onClick={onClose}>
            View final board
          </button>
        </footer>
      </div>
    </div>
  );
}
